import { ChiefAPIError } from "@api/types/backendTypes";
import axiosInstance from "@/lib/axios";
import { useAuth } from "../useAuth";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { z } from "zod";

const GetPostbackSourceStatusSchema = z.object({
  organisationId: z.string(),
});

export type PostbackSourceStatusAPIResponse = {
  id: string;
  name?: string;
  active_for_postback?: string[];
  created?: string;
  updated?: string;
};

export type PostbackSourceStatus = {
  id: string;
  name?: string;
  activeForPostback: string[];
  created?: string;
  updated?: string;
};

export const postbackSourceStatusQueryKey = (organisationId: string) => [
  "postbackSourceStatus",
  organisationId,
];

export const transformToPostbackSourceStatus = (
  data: PostbackSourceStatusAPIResponse
): PostbackSourceStatus => {
  const status: PostbackSourceStatus = {
    id: data.id,
    activeForPostback: data.active_for_postback ?? [],
  };

  if (data.name !== undefined) {
    status.name = data.name;
  }
  if (data.created !== undefined) {
    status.created = data.created;
  }
  if (data.updated !== undefined) {
    status.updated = data.updated;
  }

  return status;
};

async function fetchPostbackSourceStatus(
  organisationId: string
): Promise<PostbackSourceStatus> {
  try {
    GetPostbackSourceStatusSchema.parse({ organisationId });
    const response = await axiosInstance.get(
      `${process.env.NEXT_PUBLIC_AD_CONNECTOR_V1_BASE_URL}/organisations/${organisationId}/get`
    );

    if (response.status !== 200) {
      throw new Error(response.data.error);
    }

    return transformToPostbackSourceStatus(response.data);
  } catch (err: unknown) {
    if (
      err &&
      typeof err === "object" &&
      "isAxiosError" in err &&
      err.isAxiosError
    ) {
      const axiosError = err as AxiosError;
      if (!axiosError.response) {
        throw err;
      }
      const apiError = axiosError.response?.data as ChiefAPIError;
      const firstErr = apiError.detail?.[0];
      throw new Error(firstErr?.msg ?? axiosError.message);
    }
    throw new Error("Unknown error occurred!");
  }
}

export type UseGetPostbackSourceStatusOptions = {
  enabled?: boolean;
};

export function useGetPostbackSourceStatus(
  organisationId?: string,
  options?: UseGetPostbackSourceStatusOptions
) {
  const { data } = useAuth();
  const enabled =
    options?.enabled !== undefined
      ? options.enabled && !!data?.token && !!organisationId
      : !!data?.token && !!organisationId;

  return useQuery({
    queryKey: postbackSourceStatusQueryKey(organisationId ?? ""),
    queryFn: () => fetchPostbackSourceStatus(organisationId ?? ""),
    enabled,
    staleTime: 30000,
    gcTime: 30000,
    refetchOnWindowFocus: true,
  });
}
